import React from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  Loader2,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_NAMES = { cv_basic: "BASIC", cv_pro: "PRO", cv_elite: "ELITE" };

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const StatusPill = ({ status }) => {
  const styles = {
    paid: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
    completed: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
    pending: "border-amber-500/25 bg-amber-500/10 text-amber-300",
    processing: "border-blue-500/25 bg-blue-500/10 text-blue-300",
    failed: "border-red-500/25 bg-red-500/10 text-red-300",
    expired: "border-zinc-500/25 bg-zinc-500/10 text-zinc-400",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] ${styles[status] || styles.expired}`}
    >
      {status}
    </span>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = React.useState(null);
  const [orders, setOrders] = React.useState(null);
  const [users, setUsers] = React.useState(null);
  const [jobs, setJobs] = React.useState(null);
  const [denied, setDenied] = React.useState(false);

  usePageMeta({ title: "Admin | CV AI PRO", description: "Owner dashboard.", path: "/admin" });

  React.useEffect(() => {
    if (user === false) navigate("/login?next=/admin", { replace: true });
  }, [user, navigate]);

  React.useEffect(() => {
    if (!user || user === false) return;
    const load = async () => {
      try {
        const [o, ord, usr, jb] = await Promise.all([
          axios.get(`${API}/admin/overview`, { withCredentials: true }),
          axios.get(`${API}/admin/orders`, { withCredentials: true }),
          axios.get(`${API}/admin/users`, { withCredentials: true }),
          axios.get(`${API}/admin/jobs`, { withCredentials: true }),
        ]);
        setOverview(o.data);
        setOrders(ord.data.orders);
        setUsers(usr.data.users);
        setJobs(jb.data.jobs);
      } catch (err) {
        if (err?.response?.status === 403 || err?.response?.status === 401) {
          setDenied(true);
          toast.error("Admins only", { description: "This page is restricted to the site owner." });
          setTimeout(() => navigate("/", { replace: true }), 1200);
        }
      }
    };
    load();
  }, [user, navigate]);

  if (!user || user === false || denied) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  const stats = overview
    ? [
        { label: "Revenue", value: `$${overview.revenue_usd.toFixed(2)}`, Icon: Wallet, testid: "admin-stat-revenue" },
        { label: "Paid orders", value: overview.paid_orders, Icon: CreditCard, testid: "admin-stat-orders" },
        { label: "Customers", value: overview.total_users, Icon: Users, testid: "admin-stat-users" },
        { label: "CVs generated", value: overview.jobs_completed, Icon: FileText, testid: "admin-stat-jobs" },
      ]
    : [];

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="admin-page">
      <div aria-hidden className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-indigo-600/15 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg opacity-50" />

      <header className="relative z-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="admin-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-14 pb-24">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Owner dashboard
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter text-white" data-testid="admin-headline">
            Business at a glance.
          </h1>
        </motion.div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5"
              data-testid={s.testid}
            >
              <s.Icon className="h-4 w-4 text-zinc-500" />
              <p className="mt-3 text-3xl font-semibold tracking-tighter text-white">{s.value}</p>
              <p className="mt-1 text-xs text-zinc-500">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Orders */}
        <section className="mt-8 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 sm:p-8" data-testid="admin-orders-section">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Orders</p>
          {orders === null ? (
            <Loader2 className="mt-6 h-5 w-5 animate-spin text-zinc-600" />
          ) : orders.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No orders yet.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
                    <th className="pb-3 pr-4 font-medium">Customer</th>
                    <th className="pb-3 pr-4 font-medium">Plan</th>
                    <th className="pb-3 pr-4 font-medium">Amount</th>
                    <th className="pb-3 pr-4 font-medium">Payment</th>
                    <th className="pb-3 pr-4 font-medium">CV</th>
                    <th className="pb-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.session_id} className="border-b border-white/5 last:border-0" data-testid={`admin-order-${o.session_id.slice(-8)}`}>
                      <td className="py-3 pr-4">
                        <p className="text-white text-[13px]">{o.customer_name || "—"}</p>
                        <p className="text-zinc-500 text-[11px]">{o.customer_email || "—"}</p>
                      </td>
                      <td className="py-3 pr-4 text-zinc-300">{PLAN_NAMES[o.lookup_key] || o.lookup_key}</td>
                      <td className="py-3 pr-4 text-white">${((o.amount || 0) / 100).toFixed(2)}</td>
                      <td className="py-3 pr-4"><StatusPill status={o.payment_status} /></td>
                      <td className="py-3 pr-4">
                        {o.job_status ? <StatusPill status={o.job_status} /> : <span className="text-[11px] text-zinc-600">not used</span>}
                      </td>
                      <td className="py-3 text-zinc-500 text-xs">{fmtDate(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Customers */}
        <section className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 sm:p-8" data-testid="admin-users-section">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Customers</p>
          {users === null ? (
            <Loader2 className="mt-6 h-5 w-5 animate-spin text-zinc-600" />
          ) : users.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No customers yet.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
                    <th className="pb-3 pr-4 font-medium">Name</th>
                    <th className="pb-3 pr-4 font-medium">Email</th>
                    <th className="pb-3 pr-4 font-medium">Paid orders</th>
                    <th className="pb-3 pr-4 font-medium">CVs</th>
                    <th className="pb-3 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-white/5 last:border-0" data-testid={`admin-user-${u.id.slice(0, 8)}`}>
                      <td className="py-3 pr-4 text-white text-[13px]">{u.name || "—"}</td>
                      <td className="py-3 pr-4 text-zinc-400 text-[13px]">{u.email}</td>
                      <td className="py-3 pr-4 text-zinc-300">{u.paid_orders}</td>
                      <td className="py-3 pr-4 text-zinc-300">{u.cv_jobs}</td>
                      <td className="py-3 text-zinc-500 text-xs">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* CV Jobs */}
        <section className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 sm:p-8" data-testid="admin-jobs-section">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Generated CVs</p>
          {jobs === null ? (
            <Loader2 className="mt-6 h-5 w-5 animate-spin text-zinc-600" />
          ) : jobs.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No CVs generated yet.</p>
          ) : (
            <div className="mt-4 space-y-2.5">
              {jobs.map((j) => (
                <div
                  key={j.id}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-zinc-950/60 p-4"
                  data-testid={`admin-job-${j.id.slice(0, 8)}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{j.full_name} {j.desired_role ? `· ${j.desired_role}` : ""}</p>
                    <p className="text-[11px] text-zinc-500">
                      {j.email} · {PLAN_NAMES[j.plan] || j.plan} · {fmtDate(j.created_at)} · {j.generations_used}/{j.max_generations} generations
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {j.status === "completed" ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
                        <CheckCircle2 className="h-3.5 w-3.5" /> done
                      </span>
                    ) : j.status === "processing" ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-blue-300">
                        <Clock className="h-3.5 w-3.5" /> processing
                      </span>
                    ) : (
                      <StatusPill status={j.status} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
